import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useActions, useLive, useNow, useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import { addDays, formatClock, formatFreshness, formatMoney, isoDayIn } from '../mirror/time';
import { DepartureDetail, DepartureList, WeatherCalled } from '../wire';
import type {
  DepartureDetail as DepartureDetailType,
  DepartureInstance,
  WeatherCalled as WeatherCalledType,
  WireIdentity,
} from '../wire';
import { useLocale, useT } from '../messages';
import { NewAlbum } from './Albums';
import type { T, UiLocale } from '../messages';

/**
 * **Departures** — the captain's day (spec §4.8), the dock at six in the
 * morning.
 *
 * §4.8 asks the operator surface for two questions and one action, and this
 * screen is exactly those three things. *What is going out today, and who is on
 * it* — the day list and the manifest, which §4.8 itself calls "the Orders-view
 * pattern (§3.6) for boats". And *this one is not going out* — the weather call.
 *
 * **Online, like the pass.** Seats are counted from the book on every read
 * (there is no `seats_claimed` column, deliberately — migration 0020), so a
 * mirrored sailing would be a seat count that cannot drift *on the server* and
 * drifts freely on the device. See `useRemote.ts` for the general argument.
 *
 * ## The weather call names its consequence before it happens
 *
 * The button cancels every booking on the manifest and emits an event for each,
 * in one transaction — so the confirm step says how many parties and how many
 * seats, and that the partner will be told. That sentence is built from the
 * instance the screen is already showing rather than from a preflight call: the
 * numbers are the ones on screen, which is the only pair a person can check.
 *
 * **No fee, ever**, and the copy says so. §4.8 is explicit that being blown out
 * is the operator's call rather than the guest's change of mind, and that a
 * refund of money already taken is the owner's action in their own PayPal
 * dashboard. Reading that off the screen is the difference between a captain who
 * knows what the button did and one who finds out on Monday.
 *
 * ## The seat sold from the manifest (V5, verticals-plan D6)
 *
 * The manifest sheet is also where the next seat is sold, because the person
 * with the sheet open is the person a walk-up asks. The tiers arrive priced on
 * the manifest read, the sheet multiplies them by a seat count to show a total
 * **before the button**, and the intent carries tier ids and seat counts and
 * nothing else — the server sums the same catalog again when it sells the seats
 * (B7). The waiver, when the tenant publishes one, is read to the guest off this
 * sheet and ticked on their word; the server records that tick the way it
 * records the website's, anchored to the version it holds at that instant.
 */

export function Departures(): ReactNode {
  const runtime = useRuntime();
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? isoDayIn(timezone, now);
  const [openInstance, setOpenInstance] = useState<string | null>(null);
  /** Which sailing's album sheet is open, if any (tour-photos decision 5). */
  const [albumFor, setAlbumFor] = useState<string | null>(null);
  /**
   * Whether this tenant bought photos. A dive shop with no photographer sees no
   * Album button at all — the same "hidden, not disabled" rule the tab row
   * follows, one level down.
   */
  const photos = useHasModule('photos');

  /** Hidden by `App`; silent here. See `Orders.tsx` for why it is checked twice. */
  const entitled = useHasModule('departures');

  const departures = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/departures/instances',
        query: { date_from: day, date_to: day },
        schema: DepartureList,
        signal,
      }),
    [day],
    // Seats move when bookings move, and bookings are `booking.*` on the stream.
    {
      cue: (type) => type.startsWith('booking.') || type.startsWith('departure.'),
      enabled: entitled,
    },
  );

  const rows = departures.data?.departures ?? [];

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('departures.notSwitchedOn')}</p>
      </section>
    );
  }

  return (
    <section className="view view--departures">
      <div className="view__header">
        <div className="date-picker">
          <button type="button" className="button" onClick={() => setPicked(addDays(day, -1))}>
            ‹
          </button>
          <input type="date" value={day} onChange={(e) => setPicked(e.target.value)} />
          <button type="button" className="button" onClick={() => setPicked(addDays(day, 1))}>
            ›
          </button>
        </div>
        <h1>{t('departures.title')}</h1>
        <span className="status-strip__staleness">
          {`${formatFreshness(departures.fetchedAt, now, timezone, locale)}${
            departures.error === null ? '' : t('departures.couldNotReach')
          }`}
        </span>
      </div>

      {departures.loading && departures.fetchedAt === null ? (
        <p className="empty">{t('departures.reading')}</p>
      ) : null}

      {!departures.loading && rows.length === 0 ? (
        <p className="empty">{t('departures.noneOn', { day })}</p>
      ) : (
        <ul className="day-list">
          {rows.map((instance) => (
            <li
              key={instance.departure_instance_id}
              className={`day-row departure-row departure-row--${instance.status}`}
            >
              <div className="day-row__time">
                <strong>{formatClock(instance.slot.start, timezone, locale)}</strong>
                <small>{formatClock(instance.slot.end, timezone, locale)}</small>
              </div>

              <div className="day-row__who">
                {/* `service_name` and `vessel` are the operator's own words. */}
                <strong>{instance.service_name}</strong>
                <small>
                  {instance.vessel ?? t('departures.noVessel')}
                  {instance.private ? t('departures.privateCharter') : ''}
                </small>
                {isCancelled(instance) ? (
                  <small className="muted">
                    {instance.status === 'cancelled_weather'
                      ? t('departures.weather')
                      : t('departures.cancelled')}
                    {instance.cancellation_reason === null
                      ? ''
                      : t('departures.reasonTail', { reason: instance.cancellation_reason })}
                  </small>
                ) : null}
              </div>

              <div className="day-row__tail">
                <span className="badge">
                  {t('departures.seats', {
                    claimed: instance.seats_claimed,
                    total: instance.seats_total,
                  })}
                </span>
                <small className="muted">
                  {t('departures.parties', { count: instance.bookings })}
                </small>
                <div className="row-actions">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setOpenInstance(instance.departure_instance_id)}
                  >
                    {t('departures.manifest')}
                  </button>
                  {/*
                    "Today's sailing's album" (docs/tour-photos-design.md
                    decision 5) — a **sheet in place**, not a jump to the Albums
                    tab.
                    
                    This codebase reaches for sheets rather than navigation
                    (`ManifestSheet`, `PaymentLinksPanel`), and here that is more
                    than consistency: the captain is standing on a dock with the
                    manifest open and one hand free. Sending them to another tab
                    and asking them to find today's trip in a list is three taps
                    to do what the row already knows.
                  */}
                  {photos ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setAlbumFor(instance.departure_instance_id)}
                    >
                      {t('albums.forSailing')}
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {albumFor === null ? null : (
        <div className="sheet" role="dialog" aria-label={t('albums.dialog')}>
          <div className="sheet__panel">
            <h2>{t('albums.forSailing')}</h2>
            {/*
              The anchor is passed, never typed: `departure_instance_id` is a
              `di_` ULID and the only caller that holds one is this row.
            */}
            <NewAlbum departureInstanceId={albumFor} />
            <div className="sheet__actions">
              <button type="button" className="button" onClick={() => setAlbumFor(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {openInstance === null ? null : (
        <ManifestSheet
          instanceId={openInstance}
          timezone={timezone}
          currency={currency}
          t={t}
          locale={locale}
          onClose={() => setOpenInstance(null)}
          onChanged={() => departures.reload()}
        />
      )}
    </section>
  );
}

function isCancelled(instance: DepartureInstance): boolean {
  return instance.status === 'cancelled' || instance.status === 'cancelled_weather';
}

/* ------------------------------------------------------------------ */
/* The manifest, and the weather call                                  */
/* ------------------------------------------------------------------ */

function ManifestSheet({
  instanceId,
  timezone,
  currency,
  t,
  locale,
  onClose,
  onChanged,
}: {
  instanceId: string;
  timezone: string;
  currency: string;
  t: T;
  locale: UiLocale;
  onClose: () => void;
  onChanged: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);

  const detail = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: `/departures/instances/${instanceId}`,
        schema: DepartureDetail,
        signal,
      }),
    [instanceId],
    { cue: (type) => type.startsWith('booking.'), pollMs: 30_000 },
  );

  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<'weather' | 'operator'>('weather');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [called, setCalled] = useState<WeatherCalledType | null>(null);
  /** The seat sale (V5): the form is open, and what it last said. */
  const [selling, setSelling] = useState(false);
  const [sold, setSold] = useState<string | null>(null);
  const canWrite = session?.can_write === true;

  const instance = detail.data;

  const call = useCallback(async (): Promise<void> => {
    setSending(true);
    setFailure(null);
    try {
      const answer = await runtime.api.request({
        method: 'POST',
        path: `/departures/instances/${instanceId}/weather-call`,
        body: {
          reason,
          kind,
          ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
        },
        // A captain taps this on a phone on marina wifi. The memo makes a
        // replay the first answer rather than a second round of apologies to
        // forty guests — `routes/departures.ts` says exactly that.
        idempotencyKey: ulid(),
        schema: WeatherCalled,
      });
      setCalled(answer);
      setConfirming(false);
      detail.reload();
      onChanged();
    } catch (e) {
      // The server's sentence about this exact sailing, verbatim.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }, [runtime, instanceId, reason, kind, session, detail, onChanged]);

  return (
    <div className="sheet" role="dialog" aria-label={t('departures.manifest')}>
      <div className="sheet__panel sheet__panel--wide">
        {instance === null ? (
          <p className="empty">
            {detail.error === null ? t('departures.readingManifest') : t('departures.notFound')}
          </p>
        ) : (
          <>
            <h2>
              {t('departures.sheetTitle', {
                name: instance.service_name,
                clock: formatClock(instance.slot.start, timezone, locale),
              })}
            </h2>
            <p className="muted">
              {t('departures.summaryLine', {
                vessel: instance.vessel ?? t('departures.noVessel'),
                claimed: instance.seats_claimed,
                total: instance.seats_total,
                parties: t('departures.parties', { count: instance.bookings }),
              })}
              {isCancelled(instance)
                ? t('departures.cancelledTail', {
                    word:
                      instance.status === 'cancelled_weather'
                        ? t('departures.weatherLower')
                        : t('departures.cancelledLower'),
                    reason: instance.cancellation_reason,
                  })
                : ''}
            </p>

            <ul className="report-list manifest-list">
              {instance.manifest.map((entry) => (
                <li
                  key={entry.booking_id}
                  className={entry.status === 'cancelled' ? 'is-cancelled' : undefined}
                >
                  <div>
                    {/* Guest names, tier names and notes are all data. */}
                    <strong>{entry.guest_name}</strong>
                    <small>
                      {t('departures.seatCount', { count: entry.party_size })}
                      {entry.seats.length === 0
                        ? ''
                        : t('departures.lanes', { lanes: entry.seats.join(', ') })}
                      {entry.tickets.length === 0
                        ? ''
                        : t('departures.ticketsTail', {
                            tickets: entry.tickets
                              .map((ticket) =>
                                t('departures.ticketLine', {
                                  seats: ticket.seats,
                                  name: ticket.name,
                                }),
                              )
                              .join(', '),
                          })}
                      {/* The gangway fact (owner's ruling 6). A cancelled row
                          keeps whatever it had: a party that acknowledged and
                          then cancelled did both, and a manifest is the record
                          of what happened rather than of who is coming. */}
                      {entry.waiver == null
                        ? t('departures.waiverNone')
                        : t('departures.waiverOk', { version: entry.waiver.version })}
                    </small>
                    {entry.notes === '' ? null : <small className="muted">{entry.notes}</small>}
                  </div>
                  <span>
                    {formatMoney(entry.value_cents, currency, locale)}
                    {entry.status === 'cancelled' ? t('departures.entryCancelled') : ''}
                    {/* After a call, one word per party (V7 / D4): who was
                        texted, and who the desk still has to ring. Read from
                        the manifest — the events the call wrote — so a sheet
                        reopened later says the same thing. */}
                    {entry.weather_notice == null
                      ? ''
                      : entry.weather_notice === 'texted'
                        ? t('departures.noticeTexted')
                        : entry.weather_notice === 'no_consent'
                          ? t('departures.noticeNoConsent')
                          : t('departures.noticeNoPhone')}
                  </span>
                </li>
              ))}
              {instance.manifest.length === 0 ? (
                <li className="muted">{t('departures.nobodyBooked')}</li>
              ) : null}
            </ul>

            {called !== null ? (
              <p className="note" role="status">
                {t('departures.called', {
                  bookings: called.bookings_cancelled.length,
                  seats: called.seats_released,
                })}
                {called.bookings_cancelled.length === 0
                  ? ''
                  : t('departures.calledTexted', {
                      texted: called.guests_texted,
                      notTexted: called.guests_not_texted,
                    })}
              </p>
            ) : null}

            {sold === null ? null : (
              <p className="note" role="status">
                {sold}
              </p>
            )}

            {/* The sale, only on a sailing that is going out with room on it,
                and only for a credential that may write: hidden, not disabled,
                the same rule the tab row follows. A manifest read from a server
                that sends no tiers has nothing to sell from and draws nothing. */}
            {!isCancelled(instance) &&
            called === null &&
            canWrite &&
            instance.seats_remaining > 0 &&
            instance.tiers.length > 0 ? (
              selling ? (
                <SellSeatForm
                  instance={instance}
                  timezone={timezone}
                  currency={currency}
                  t={t}
                  locale={locale}
                  onClose={() => setSelling(false)}
                  onSold={(line) => {
                    setSold(line);
                    setSelling(false);
                    detail.reload();
                    onChanged();
                  }}
                />
              ) : (
                <div className="row-actions">
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => setSelling(true)}
                  >
                    {t('departures.sellSeat')}
                  </button>
                </div>
              )
            ) : null}

            {failure === null ? null : (
              <p className="error" role="alert">
                {failure}
              </p>
            )}

            {isCancelled(instance) || called !== null ? null : confirming ? (
              <div className="editor editor--inline">
                {/*
                  The consequence, quoted from the row on screen. §4.8's cascade
                  is one transaction — the sailing, every booking on it, and an
                  outbox event per guest — so the sentence names all three
                  rather than saying "are you sure".
                */}
                <p className="note">
                  {t('departures.confirmLead')}
                  <strong>{instance.bookings}</strong>
                  {t('departures.confirmMid', { count: instance.bookings })}
                  <strong>{instance.seats_claimed}</strong>
                  {t('departures.confirmTail', { count: instance.seats_claimed })}
                </p>
                <label className="field">
                  <span>{t('departures.why')}</span>
                  <input
                    type="text"
                    value={reason}
                    maxLength={500}
                    placeholder={t('departures.reasonPlaceholder')}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <label className="field field--row">
                  <span>{t('departures.reasonKind')}</span>
                  <select
                    value={kind}
                    onChange={(e) =>
                      setKind(e.target.value === 'operator' ? 'operator' : 'weather')
                    }
                  >
                    <option value="weather">{t('departures.kindWeather')}</option>
                    <option value="operator">{t('departures.kindOperator')}</option>
                  </select>
                </label>
                <div className="sheet__actions">
                  <button type="button" className="button" onClick={() => setConfirming(false)}>
                    {t('departures.keepIt')}
                  </button>
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={sending || reason.trim() === ''}
                    onClick={() => void call()}
                  >
                    {sending
                      ? t('departures.calling')
                      : t('departures.cancelN', { count: instance.bookings })}
                  </button>
                </div>
              </div>
            ) : (
              <div className="row-actions">
                <button
                  type="button"
                  className="button button--ghost link-button--danger"
                  onClick={() => setConfirming(true)}
                >
                  {t('departures.weatherCall')}
                </button>
              </div>
            )}
          </>
        )}

        <div className="sheet__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Selling a seat from the manifest (V5, verticals-plan D6)             */
/* ------------------------------------------------------------------ */

/**
 * "Two certified and a snorkeller on the 08:30."
 *
 * Tiers × seats, with the total the instance's tiers make of it shown beside the
 * button — and never sent. The guest is named as `BookSheet` names one: somebody
 * in the book, or a name and a number on the dock, and a walk-up with no number
 * is refused here with `resolveGuest`'s sentence rather than an hour later from
 * the task list.
 *
 * **The waiver line** appears only when the manifest carries one, which is only
 * when the tenant publishes one. It shows the document whole — the desk reads
 * it to the guest — and a box that says what the tick means: *read to the guest
 * and acknowledged at the desk*. The server refuses the sale without it
 * (`waiver_required`), so the form refuses first, in the same words.
 *
 * Queued through the outbox and awaited, for `BookStaySheet`'s reason: a sale
 * on marina wifi is the one thing a tunnel must not lose.
 */
function SellSeatForm({
  instance,
  timezone,
  currency,
  t,
  locale,
  onClose,
  onSold,
}: {
  instance: DepartureDetailType;
  timezone: string;
  currency: string;
  t: T;
  locale: UiLocale;
  onClose: () => void;
  onSold: (line: string) => void;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const clients = useLive(() => runtime.mirror.clients(), []);

  const [seats, setSeats] = useState<Record<string, number>>({});
  const [waiverTicked, setWaiverTicked] = useState(false);
  const [clientId, setClientId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const picked = instance.tiers
    .map((tier) => ({ tier, count: seats[tier.tier_id] ?? 0 }))
    .filter((row) => row.count > 0);
  const seatCount = picked.reduce((sum, row) => sum + row.count, 0);
  // Shown, never sent: the server sums the same catalog when it sells.
  const totalCents = picked.reduce((sum, row) => sum + row.count * row.tier.unit_price_cents, 0);
  const waiver = instance.waiver ?? null;
  const client = clients.find((c) => c.client_id === clientId);

  const submit = async (): Promise<void> => {
    setError(null);
    if (seatCount === 0) return setError(t('departures.errorSeats'));
    if (waiver !== null && !waiverTicked) return setError(t('departures.errorWaiver'));

    const namedClient = client !== undefined;
    const walkIn = !namedClient && guestName.trim() !== '';
    if (!namedClient && !walkIn) return setError(t('book.errorName'));
    const identities: WireIdentity[] =
      guestPhone.trim() === '' ? [] : [{ identity_kind: 'phone', identity: guestPhone.trim() }];
    if (walkIn && identities.length === 0) return setError(t('book.errorContact'));

    const clientName = namedClient ? (client?.display_name ?? '') : guestName.trim();
    setSending(true);
    try {
      const entry = await actions.sellSeat({
        instanceId: instance.departure_instance_id,
        tripName: instance.service_name,
        whenIso: instance.slot.start,
        tickets: picked.map((row) => ({ tierId: row.tier.tier_id, seats: row.count })),
        notes: notes.trim() === '' ? undefined : notes.trim(),
        source: walkIn ? 'walk_in' : 'staff',
        ...(waiver === null ? {} : { waiverAccepted: waiverTicked }),
        clientName,
        ...(namedClient ? { clientId } : { guest: { name: guestName.trim(), identities } }),
      });
      await runtime.outbox.drain();
      const settled = (await runtime.outbox.all()).find((e) => e.id === entry.id);
      if (settled?.status === 'rejected') {
        setError(settled.rejection?.message ?? settled.lastError ?? t('departures.notFound'));
        return;
      }
      onSold(
        settled?.status === 'done'
          ? t('departures.sold', { name: clientName, count: seatCount })
          : t('departures.queued'),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="editor editor--inline" aria-label={t('departures.sellTitle')}>
      <h3>{t('departures.sellTitle')}</h3>
      <p className="muted">
        {formatClock(instance.slot.start, timezone, locale)}
        {' · '}
        {t('departures.seatsLeft', { count: instance.seats_remaining })}
      </p>

      {instance.tiers.map((tier) => (
        <label className="field field--row" key={tier.tier_id}>
          <span>
            {t('departures.tierSeats', {
              name: tier.name,
              price: formatMoney(tier.unit_price_cents, currency, locale),
            })}
          </span>
          <input
            type="number"
            min={0}
            max={instance.seats_remaining}
            disabled={tier.sold_out}
            value={seats[tier.tier_id] ?? 0}
            onChange={(e) =>
              setSeats((prev) => ({
                ...prev,
                [tier.tier_id]: Math.max(
                  0,
                  Math.min(instance.seats_remaining, Number(e.target.value) || 0),
                ),
              }))
            }
          />
        </label>
      ))}

      <div className="checkout-total" role="status">
        <span className="checkout-total__sum">{formatMoney(totalCents, currency, locale)}</span>
        <small className="muted">{t('departures.total')}</small>
        <small className="muted">{t('departures.totalNote')}</small>
      </div>

      {waiver === null ? null : (
        <div className="panel">
          <strong>
            {t('departures.waiverTitle', { title: waiver.title, version: waiver.version })}
          </strong>
          {/* The document whole, for the desk to read out — not a summary, not
              a link (owner's ruling 6). */}
          <p className="note">{waiver.text}</p>
          <label className="field field--row">
            <input
              type="checkbox"
              checked={waiverTicked}
              onChange={(e) => setWaiverTicked(e.target.checked)}
            />
            <span>{t('departures.waiverTick')}</span>
          </label>
        </div>
      )}

      <label className="field">
        <span>{t('book.existingGuest')}</span>
        <select
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            if (e.target.value !== '') setGuestName('');
          }}
        >
          <option value="">{t('book.someoneNew')}</option>
          {clients
            .filter((c) => !c.archived)
            .map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.display_name}
              </option>
            ))}
        </select>
      </label>
      {clientId === '' ? (
        <>
          <label className="field">
            <span>{t('common.name')}</span>
            <input value={guestName} onChange={(e) => setGuestName(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('clients.phone')}</span>
            <input
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              inputMode="tel"
            />
          </label>
        </>
      ) : null}

      <label className="field">
        <span>{t('common.notes')}</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>

      {error === null ? null : (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="sheet__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={sending || seatCount === 0}
          onClick={() => void submit()}
        >
          {sending ? t('departures.selling') : t('departures.sell', { count: seatCount })}
        </button>
      </div>
    </div>
  );
}
